import React from "react";
import {
    closestCenter,
    DndContext,
    DragEndEvent,
    DragStartEvent,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors
} from "@dnd-kit/core";
import {
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy
} from "@dnd-kit/sortable";
import {CSS} from "@dnd-kit/utilities";
import {useTranslation} from "react-i18next";
import "../../i18n/config";
import {Tooltip} from "../common/Tooltip";
import {useSetStyleRegistory} from "../../hooks/useSetStyleRegistory";
import {RoleKey} from "../../models/Keys";
import {RoleHeader, RoleHeaders} from "../../models/RoleHeader";
import {InlineEditor} from "./InlineEditor";


type RCRoleHeaderProps = {
    role_headers: RoleHeaders;
    line_header_width: number;
    readOnly: boolean;
    onNameChange: (role_key: RoleKey, name: string) => void;
    onRemove: (role_key: RoleKey) => void;
    onMove: (from_role_key: RoleKey, to_role_key: RoleKey) => void;
}
export const RCRoleHeader = (props: RCRoleHeaderProps) => {
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const keys = props.role_headers.Keys;
    const handleDragEnd = (event: DragEndEvent) => {
        const {active, over} = event;

        if (over && active.id !== over.id) {
            props.onMove(active.id as RoleKey, over.id as RoleKey);
        }
    }

    const handleDragStart = (_: DragStartEvent) => {
    }

    const style = {
        display: 'grid',
        gridTemplateColumns: props.line_header_width,
        gridTemplateRows: props.role_headers.GridTemplateRows,
        gap: 10,
    };

    return (
        <div className="role_header" style={style}>
            <DndContext sensors={sensors} collisionDetection={closestCenter}
                        onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
                <SortableContext items={keys} strategy={verticalListSortingStrategy}>
                    {props.role_headers.map((r) =>
                        <RCRoleHeaderCell key={r.Key} role_header={r}
                                          readOnly={props.readOnly}
                                          onNameChange={props.onNameChange}
                                          onRemove={props.onRemove}
                        />
                    )}
                </SortableContext>
            </DndContext>
        </div>
    );
}
type RCRoleHeaderCellProps = {
    role_header: RoleHeader;
    readOnly: boolean;
    onNameChange: (role_key: RoleKey, name: string) => void;
    onRemove: (role_key: RoleKey) => void;
};
const RCRoleHeaderCell = (props: RCRoleHeaderCellProps) => {
    const {t} = useTranslation();
    const [isEdit, setIsEdit] = React.useState(false);
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
    } = useSortable({id: props.role_header.Key});
    if (transform) {
        // y 方向に固定
        transform.x = 0;
        transform.scaleX = 1;
        transform.scaleY = 1;
    }

    const transition_string = transition === null ? '' : transition;

    const trans_style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition: transition_string,
    };

    const setStyleRegistry = useSetStyleRegistory();
    setStyleRegistry.dispatch(props.role_header.Key, trans_style);


    const handleRemoveRole = () => {
        props.onRemove(props.role_header.Key)
    }

    return (
        <div ref={setNodeRef} className="role_header_cell" style={trans_style} {...attributes}
             onClick={() => !props.readOnly && setIsEdit(true)}>
            <InlineEditor value={props.role_header.Name} isEdit={isEdit}
                          onChangeValue={(value) => {
                              if (props.role_header.Name !== value) {
                                  props.onNameChange(props.role_header.Key, value)
                              }
                          }}
                          onLeave={() => setIsEdit(false)}
            />
            <div className="grab-ui" data-enabled={!props.readOnly} onClick={e => e.stopPropagation()}>
                <Tooltip title={t("上下移動")}>
                    <span className="grab-ui-button vertical_move" {...listeners} />
                </Tooltip>
                <Tooltip title={t("削除")}>
                    <span className="grab-ui-button delete" onClick={handleRemoveRole}/>
                </Tooltip>
            </div>
        </div>
    );
}
